import type * as Types from '../../../../graphql';

import { gql } from '@apollo/client';
import { PlaybookTileFragmentDoc, JobEventTileFragmentDoc } from './playbook-fragments.gql';
export type ProjectColoredWidgetFragment = { id: number, name: string, color: string | null };

export type PlaybookEventBasicsFragment = { id: number, alias: string | null, status: string, note: string | null, created: string, supportsLogFileDownload: boolean | null, projectVisibleForUser: boolean | null, eventExpired: boolean | null, executionLogsAvailable: boolean | null, workflowName: string | null, project: { id: number, name: string, color: string | null } | null, owner: { firstName: string | null, lastName: string | null, username: string | null }, job: { id: number, name: string, status: Types.JobStatusEnum | null, created: string | null, tool: string | null, owner: { display: string | null, id: number, username: string | null, firstName: string | null, lastName: string | null, email: string | null } | null, params: { blast: { blastType: string | null } | null } | null } | null, inputs: Array<{ inputValues: Array<string | null> | null, label: string }> | null, workflowP2ExtendedConfig: { sampleType: { name: string } | null } | null, playbook: { id: number, name: string, nameSuffix: string | null, protectedPlaybook: boolean | null, description: string | null, standard: boolean | null, enabled: boolean | null, config: { icon: string | null } | null, type: { id: number | null, class: string | null }, categories: Array<{ name: string }> } };

export type UserBasicsFragment = { id: number, username: string | null, firstName: string | null, lastName: string | null, email: string | null, display: string | null };

export type RegionTileFragment = { contig: { id: number, sequenceId: string, organism: { id: number, name: string | null, species: { id: number, name: string } | null } } | null, range: { start: number, stop: number } | null };

export type OrganismTileFragment = { id: number, name: string | null, type: string | null, description: string | null, hidden: boolean, species: { name: string } | null };

export type SequenceTileFragment = { id: number, name: string | null, sequenceId: string | null, sequenceType: string | null, hidden: boolean | null, identifier: string | null, description: string | null, sequenceGroup: { name: string | null } | null, database: { mnemonic: string | null, name: string | null } | null };

export type PlaybookConfigFragment = { icon: string | null, sampleStatus: { afterSubmission: number | null, whileRunning: number | null, afterCompletion: number | null, failure: number | null } | null, experimentInput: Array<{ trackerAttributeId: number | null, workflowParamId: string | null, workflowParamDefault: string | null, description: string | null, label: string | null, correctedTypeId: number | null, correctedMandatory: boolean | null } | null> | null, sampleInput: Array<{ trackerAttributeId: number | null, workflowParamId: string | null, workflowParamDefault: string | null, description: string | null, label: string | null, correctedTypeId: number | null, correctedMandatory: boolean | null } | null> | null, ngsWorkflow: { workflowId: string | null, workflowExportMappings: Array<{ exportedWorkflowFieldName: string | null, sampleAttributeId: number | null } | null> | null, workflowImportMappings: Array<{ workflowFieldId: string | null, attributeId: number | null } | null> | null } | null };

export type EventResourcesFragment = { activity: string | null, direction: string | null, resourceType: string | null, file: { id: number, fileName: string | null, fileBrowseType: string, url: string | null, previewable: boolean, humanReadablePath: string | null, fullPath: string | null, description: string | null, created: string | null, lastModified: string | null, deleted: boolean, hidden: boolean, indexingStatus: Types.IndexStatus | null, typeDisplayName: string | null, creator: { id: number, username: string | null, firstName: string | null, lastName: string | null, email: string | null, display: string | null } | null, lastModifier: { id: number, username: string | null, firstName: string | null, lastName: string | null, email: string | null, display: string | null } | null, project: { id: number, name: string, color: string | null } | null } | null, folder: { path: string | null, humanReadablePath: string | null, deleted: boolean | null, hidden: boolean | null } | null, region: { contig: { id: number, sequenceId: string, organism: { id: number, name: string | null, species: { id: number, name: string } | null } } | null, range: { start: number, stop: number } | null } | null, organism: { id: number, name: string | null, type: string | null, description: string | null, hidden: boolean, species: { name: string } | null } | null };

export type PlaybookEventTileFragment = { id: number, alias: string | null, note: string | null, status: string, created: string, shortSummary: string | null, playbook: { id: number, name: string, nameSuffix: string | null, protectedPlaybook: boolean | null, standard: boolean | null, type: { id: number | null, name: string | null, class: string | null }, config: { icon: string | null } | null }, owner: { id: number, username: string | null, firstName: string | null, lastName: string | null, email: string | null, display: string | null }, project: { id: number, name: string, description: string | null, color: string | null } | null, job: { tool: string | null } | null, results: Array<{ resourceType: string | null, file: { fileName: string | null } | null } | null> | null };

export const ProjectColoredWidgetFragmentDoc = gql`
    fragment ProjectColoredWidget on Project {
  id
  name
  color
}
    `;
export const UserBasicsFragmentDoc = gql`
    fragment UserBasics on User {
  id
  username
  firstName
  lastName
  email
  display
}
    `;
export const PlaybookEventBasicsFragmentDoc = gql`
    fragment PlaybookEventBasics on PlaybookEvent {
  id
  project {
    ...ProjectColoredWidget
  }
  ...PlaybookTile
  alias
  status
  note
  created
  supportsLogFileDownload
  projectVisibleForUser
  eventExpired
  executionLogsAvailable
  owner {
    firstName
    lastName
    username
  }
  workflowName
  job {
    ...JobEventTile
  }
  inputs {
    inputValues
    label
  }
  workflowP2ExtendedConfig {
    sampleType {
      name
    }
  }
}
    ${ProjectColoredWidgetFragmentDoc}
${PlaybookTileFragmentDoc}
${JobEventTileFragmentDoc}`;
export const SequenceTileFragmentDoc = gql`
    fragment SequenceTile on Sequence {
  id
  name
  sequenceId
  sequenceType
  sequenceGroup {
    name
  }
  name
  hidden
  identifier
  description
  database {
    mnemonic
    name
  }
}
    `;
export const PlaybookConfigFragmentDoc = gql`
    fragment PlaybookConfig on PlaybookConfig {
  icon
  sampleStatus {
    afterSubmission
    whileRunning
    afterCompletion
    failure
  }
  experimentInput {
    trackerAttributeId
    workflowParamId
    workflowParamDefault
    description
    label
    correctedTypeId
    correctedMandatory
  }
  sampleInput {
    trackerAttributeId
    workflowParamId
    workflowParamDefault
    description
    label
    correctedTypeId
    correctedMandatory
  }
  ngsWorkflow {
    workflowId
    workflowExportMappings {
      exportedWorkflowFieldName
      sampleAttributeId
    }
    workflowImportMappings {
      workflowFieldId
      attributeId
    }
  }
}
    `;
export const RegionTileFragmentDoc = gql`
    fragment RegionTile on Region {
  contig {
    id
    sequenceId
    organism {
      id
      name
      species {
        id
        name
      }
    }
  }
  range {
    start
    stop
  }
}
    `;
export const OrganismTileFragmentDoc = gql`
    fragment OrganismTile on Organism {
  id
  name
  type
  description
  hidden
  species {
    name
  }
}
    `;
export const EventResourcesFragmentDoc = gql`
    fragment EventResources on EventResource {
  activity
  direction
  file {
    id
    fileName
    fileBrowseType
    url
    previewable
    humanReadablePath
    fullPath
    description
    created
    lastModified
    deleted
    hidden
    indexingStatus
    creator {
      ...UserBasics
    }
    lastModifier {
      ...UserBasics
    }
    project {
      id
      name
      color
    }
    typeDisplayName
  }
  folder {
    path
    humanReadablePath
    deleted
    hidden
  }
  resourceType
  region {
    ...RegionTile
  }
  organism {
    ...OrganismTile
  }
}
    ${UserBasicsFragmentDoc}
${RegionTileFragmentDoc}
${OrganismTileFragmentDoc}`;
export const PlaybookEventTileFragmentDoc = gql`
    fragment PlaybookEventTile on PlaybookEvent {
  id
  alias
  note
  status
  created
  playbook {
    id
    name
    nameSuffix
    protectedPlaybook
    standard
    type {
      id
      name
      class
    }
    config {
      icon
    }
  }
  owner {
    id
    username
    firstName
    lastName
    email
    display
  }
  project {
    id
    name
    description
    color
  }
  job {
    tool
  }
  shortSummary
  results {
    resourceType
    file {
      fileName
    }
  }
}
    `;
